import type {FileUploadNode as FileUploadNodeType} from 'src/types/chatbot';
import type {NodeEditorProps} from './node-editor-v2';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useHistory} from 'src/rerouter';
import {useReleaseFlag} from 'src/hooks/product-flags';

import {useChatbotStateContext} from '../chatbot-state-context';

import {useWritebackMetadataV2} from 'src/hooks/useWritebackAllowedAttributes';

import {useOWBFields} from '../hooks';
import {useMutableFieldStatus} from '../api-hooks';

import RichEditorWithWarning from 'src/components/conversation-builder/lib/rich-editor-with-warning/rich-editor-with-warning';

import {
  createFileNode,
  createEditableGraph,
  findAncestorNodeVariables,
  getHasAncestorJobMatchQuestionNode,
  getIsUsingDiscoverAttribute,
} from '../utils';

import {validateNode} from '../node-editor/validation';

import {Button} from 'src/designSystem2021Components/buttons';

import {
  MessageDraftEditor,
  upgradePossibleEntitiesInRDCS,
} from 'src/components/conversation-builder/draft-editor';

import LoadedVariablePicker from '../node-editor/variable-picker';

import {Input} from 'src/designSystem2021Components/input';

import {useEditorState, useChatbotVariables} from '../node-editor/reducer';

import css from './node-editor-v2.css';
import {useSelector} from 'react-redux';
import {selectEntityTypeMappingsByName} from 'src/selectors/ats-entities';
import {getDiscoverAttributeWarningMessage} from 'src/components/conversation-builder/constants';
import {type RawDraftContentState, type DraftDecoratorType} from 'draft-js';


export default function FileUploadNode({
  graph,
  onCancel,
  readOnly,
  isSavingNode,
  setIsSavingNode,
}: NodeEditorProps) {
  const {t} = useLabelTranslation();
  const richTextEditorEnabled = useReleaseFlag('chatbotFlowRichEditor');
  const richTextEditorFormattingEnabled = useReleaseFlag(
    'chatbotFlowRichEditorFormatting',
  );
  const atsMappings = useSelector(selectEntityTypeMappingsByName);
  const fileUploadWritebackEnabled = useReleaseFlag('chatbotFileWriteback');
  const allowMeetingNodeVariables = Boolean(
    useReleaseFlag('chatbotMeetingNodeVariable'),
  );
  const chatbotNewHyperlinkModalEnabled = useReleaseFlag(
    'chatbotNewHyperlinkModal',
  );
  const router = useHistory();
  const {state, dispatch} = useChatbotStateContext();
  const [hasSubmitted, setHasSubmitted] = React.useState(false);
  // const [defaultNode, setDefaultNode] = React.useState(null);
  const editingNodeData = graph.nodes.find(({id}) => id === state.editingNode);
  const defaultNode =
    state.editingNode !== null && editingNodeData?.type === 'file_upload'
      ? editingNodeData
      : createFileNode();

  const {isFieldReadOnly, canFlowUpdate} = useMutableFieldStatus(
    readOnly,
    defaultNode.type,
  );

  const edgeId = Number(state.insertingNode?.edgeId || state.editingNode);

  const [editorState, editorDispatch] = useEditorState<FileUploadNodeType>({
    defaultNode,
  });

  const {node} = editorState;

  const setNode = (payload: FileUploadNodeType) => {
    editorDispatch({type: 'update', payload});
  };

  const editableGraph = createEditableGraph(graph);

  const nodeVariables = findAncestorNodeVariables(
    editableGraph,
    defaultNode,
    edgeId,
    false,
    false,
    allowMeetingNodeVariables,
  );

  const mappedNodeVariables = nodeVariables.map((nodeVar) => ({
    ...nodeVar,
    key: nodeVar.node_id,
    label: nodeVar.name,
  }));

  const hasAncestorJobMatchQuestionNode = getHasAncestorJobMatchQuestionNode(
    editableGraph,
    defaultNode,
    state.insertingNode?.edgeId,
  );

  const dynamicLabels = useChatbotVariables(graph.anchor_entity_type, {
    showDiscoverAttributes: hasAncestorJobMatchQuestionNode,
    showExternalVariables: Boolean(graph.flow_config.api_bot_enabled),
  });

  const writebackAttributes = useWritebackMetadataV2(
    graph.anchor_entity_type,
    // @ts-expect-error - TS2367 - This comparison appears to be unintentional because the types 'WritebackDataType' and '"file"' have no overlap.
  )?.filter(({data_type}) => data_type === 'file');

  const {getWbFieldId} = useOWBFields();
  const attributeWritebackInfo =
    node.writeback_field_id && writebackAttributes
      ? [...writebackAttributes.values()].find(
          (metadata) => getWbFieldId(metadata) === node.writeback_field_id,
        )
      : null;

  const validation = validateNode(node, editableGraph);
  const error = hasSubmitted ? validation : null;

  const handleSaveNode = () => {
    if (validation) {
      setHasSubmitted(true);
    } else {
      state.insertingNode
        ? dispatch({
            type: 'COMMIT_NODE',
            payload: node,
          })
        : dispatch({
            type: 'UPDATE_NODE',
            payload: node,
          });
    }
  };

  const prepareContentOnLoad = (
    rdcs: RawDraftContentState,
    decorator: DraftDecoratorType,
  ) =>
    upgradePossibleEntitiesInRDCS(
      rdcs,
      editableGraph,
      nodeVariables,
      dynamicLabels,
      decorator,
    );

  React.useEffect(() => {
    if (isSavingNode) {
      handleSaveNode();
      setIsSavingNode(false);
    }
  }, [isSavingNode]);

  const isUsingDiscoverAttribute = getIsUsingDiscoverAttribute(
    node.text_draft_json,
  );

  const DISCOVER_ATTRIBUTE_WARNING_MESSAGE =
    getDiscoverAttributeWarningMessage(t);

  return (
    <div className={css.questionNodeContainer}>
      <div className={css.section}>
        <Input
          label={t('Node_Title', 'Node Title')}
          value={node.name}
          onChange={(event) =>
            setNode({...node, name: event.currentTarget.value})
          }
        />
        {error?.name && (
          <div className={css.errorText}>
            {error.name
              ? error.name.errors?.includes('EMPTY')
                ? `${t(
                    'This_node_must_have_a_name',
                    'This node must have a name',
                  )}.`
                : `${t(
                    'This_name_is_already_taken',
                    'This name is already taken',
                  )}.`
              : `${t(
                  'Node_names_must_be_unique_40_characters_maximum',
                  'Node names must be unique. 40 characters maximum',
                )}.`}
          </div>
        )}
      </div>
      <div className={css.section}>
        <div className={css.inputLabel}>{`${t('Message', 'Message')}*`}</div>
        {richTextEditorEnabled ? (
          <RichEditorWithWarning
            warningMessage={
              isUsingDiscoverAttribute ? DISCOVER_ATTRIBUTE_WARNING_MESSAGE : ''
            }
            defaultValue={node.text_draft_json}
            onChange={(text_draft_json) => {
              setNode({...node, text_draft_json});
            }}
            editorHeight="200px"
            includeFormatting={Boolean(richTextEditorFormattingEnabled)}
            includeHyperlink={!chatbotNewHyperlinkModalEnabled}
            includeHyperlinkV2={Boolean(chatbotNewHyperlinkModalEnabled)}
            includeGif
            // @ts-expect-error - TS2322 - Type '{ key: number; label: string; name: string; node_id: number; node_name: string; derived_field_name?: string | undefined; template: string; node_type?: string | undefined; attribute_name?: string | undefined; }[]' is not assignable to type 'MenuOption[]'.
            nodeVariables={mappedNodeVariables}
            atsVariables={
              graph.use_case === 'sourcing' ? undefined : dynamicLabels
            }
            readOnly={isFieldReadOnly('text_draft_json')}
            entityType={graph.anchor_entity_type}
            prepareContentOnLoad={prepareContentOnLoad}
            atsTooltip={
              hasAncestorJobMatchQuestionNode
                ? `${t('ATS_External_variables', 'ATS & External variables')}`
                : `${t('ATS_variables', 'ATS variables')}`
            }
            enableDraftJSONCopy
          />
        ) : (
          <MessageDraftEditor
            placeholder={`${t('Enter_a_message', 'Enter a message')}...`}
            defaultValue={node.text_draft_json}
            onChange={(text_draft_json) => setNode({...node, text_draft_json})}
            entityType={graph.anchor_entity_type}
            isFirstSmsMessage={false}
            label=""
            extraCharacterCount={0}
            useCase={graph.use_case}
            nodeVariables={nodeVariables}
            readOnly={isFieldReadOnly('text_draft_json')}
            showDiscoverAttributes={hasAncestorJobMatchQuestionNode}
            showExternalVariables={Boolean(graph.flow_config.api_bot_enabled)}
            warningMessage={
              isUsingDiscoverAttribute ? DISCOVER_ATTRIBUTE_WARNING_MESSAGE : ''
            }
          />
        )}
        {error?.text_draft_json && (
          <div className={css.errorText}>{`${t(
            'Message_cannot_be_empty',
            'Message cannot be empty',
          )}.`}</div>
        )}
      </div>
      {fileUploadWritebackEnabled && (
        <div className={css.section}>
          <div className={css.inputLabel}>{`${t(
            'Select_file_type_to_upload_to_ATS',
            'Select file type to upload to ATS',
          )}:`}</div>
          <LoadedVariablePicker
            entityType={graph.anchor_entity_type}
            value={node.writeback_field_id}
            attributeMetadata={writebackAttributes}
            onChange={(attributeMetadata) => {
              const writebackFieldId = getWbFieldId(attributeMetadata);
              editorDispatch({
                type: 'set_writeback',
                payload: writebackFieldId,
                metadata: {
                  previous: attributeWritebackInfo,
                  next: attributeMetadata,
                },
              });
            }}
            readOnly={readOnly}
          />
        </div>
      )}
      <div className={css.footer}>
        <Button size="small" type="secondary" onClick={onCancel}>
          {t('Cancel', 'Cancel')}
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={handleSaveNode}
          disabled={hasSubmitted && !!validation}
        >
          {canFlowUpdate ? t('Update', 'Update') : t('Save', 'Save')}
        </Button>
      </div>
    </div>
  );
}
